import React from 'react';
import { Box, Chip, Typography, Button, CircularProgress, Tooltip } from '@mui/material';
import { Refresh, WarningAmber } from '@mui/icons-material';
import {
  CameraWithPricing,
  useProductPriceFormatters,
  getMarketPricingRefreshUi,
  isMarketPricingStale,
  formatRelativeDate,
  formatDate,
} from '../../utils/productDetailHelpers';
import { getDealerUrl } from '../../utils/dealerUrl';
import { NeutralNote } from './NeutralNote';
import { tokens } from '../../theme/tokens';
import { ResponsiveRows, ResponsiveRowsColumn } from '../ui/ResponsiveRows';

// Below `md` the three-column dealer row collapses to one column with a
// small mobile label on each cell that is not self-describing (Price, Stock);
// desktop keeps the fixed `1fr 100px 90px` template unchanged.
const dealerColumns: ResponsiveRowsColumn[] = [
  { key: 'dealer', header: 'Dealer', width: '1fr' },
  { key: 'price', header: 'Price', width: '100px', align: 'end', mobileLabel: true },
  { key: 'stock', header: 'Stock', width: '90px', align: 'end', mobileLabel: true },
];

interface ProductMarketPricingProps {
  product: CameraWithPricing;
  onRefresh: () => void;
  refreshPending: boolean;
}

export function ProductMarketPricing({ product, onRefresh, refreshPending }: ProductMarketPricingProps) {
  const { saleDisplay } = useProductPriceFormatters();
  const marketPricing = product.marketPricing;
  const dealers = marketPricing?.dealerPrices ?? [];
  const hasMarket = !!marketPricing && (marketPricing.streetPriceLow != null || dealers.length > 0);

  const refreshUi = getMarketPricingRefreshUi(marketPricing);
  const isStale = isMarketPricingStale(marketPricing);
  const checkedLabel = marketPricing?.checkedAt
    ? formatRelativeDate(marketPricing.checkedAt) || formatDate(marketPricing.checkedAt)
    : null;
  const isRefreshing = refreshUi.state === 'refreshing' || refreshPending;
  const isCooldown = refreshUi.state === 'cooldown';
  const nextDays = isCooldown ? refreshUi.nextRefreshInDays : 0;
  const refreshLabel = isRefreshing ? 'Refreshing…' : isCooldown ? `Next refresh in ${nextDays}d` : 'Refresh';
  const refreshTooltip = isRefreshing
    ? 'A market pricing refresh is already running for this product.'
    : isCooldown
      ? `Market pricing can be refreshed once every 7 days. Next refresh in ${nextDays} day${nextDays === 1 ? '' : 's'}.`
      : 'Look up current dealer and street prices. This can take up to a minute.';

  const yourPrice = saleDisplay(product).sellingPrice || product.price || 0;
  const allUnder = hasMarket && dealers.length > 0 && yourPrice > 0 && dealers.every((d) => d.price != null && d.price < yourPrice);
  const avgGap = allUnder && marketPricing?.streetPriceAvg
    ? Math.round(yourPrice - marketPricing.streetPriceAvg)
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
            Market pricing
          </Typography>
          {checkedLabel && (
            <Typography sx={{ fontSize: '11.5px', color: isStale ? tokens.signal.caution.ink : tokens.ink.faint }}>
              checked {checkedLabel}
            </Typography>
          )}
          {isStale && hasMarket && (
            <Chip
              size="small"
              label="Stale"
              sx={{
                height: 18,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                bgcolor: tokens.signal.caution.tint,
                color: tokens.signal.caution.ink,
                border: `1px solid ${tokens.signal.caution.border}`,
              }}
            />
          )}
        </Box>
        <Tooltip arrow title={refreshTooltip}>
          <span>
            <Button
              onClick={onRefresh}
              disabled={isRefreshing || isCooldown}
              startIcon={isRefreshing ? <CircularProgress size={12} /> : <Refresh sx={{ fontSize: 14 }} />}
              sx={{
                height: 28,
                px: '10px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '12.5px',
                '&:hover': { borderColor: tokens.ink.disabled, color: 'text.primary', bgcolor: 'background.paper' },
              }}
            >
              {refreshLabel}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {hasMarket ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {marketPricing?.streetPriceLow != null && marketPricing?.streetPriceHigh != null && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, fontSize: '13px' }}>
              <Typography sx={{ fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums', fontSize: 'inherit' }}>
                ${Number(marketPricing.streetPriceLow).toFixed(2)} – ${Number(marketPricing.streetPriceHigh).toFixed(2)}
              </Typography>
              <Typography sx={{ color: tokens.ink.muted, fontSize: 'inherit' }}>
                street across {dealers.length} dealer{dealers.length === 1 ? '' : 's'}
                {marketPricing.streetPriceAvg != null ? ` · avg $${Number(marketPricing.streetPriceAvg).toFixed(2)}` : ''}
              </Typography>
            </Box>
          )}

          {isStale && (
            <NeutralNote icon={<WarningAmber sx={{ fontSize: 16, color: tokens.signal.caution.solid, mt: '1px' }} />}>
              These street prices are stale{checkedLabel ? ` (${checkedLabel})` : ''}.
              Refresh before using them for MAP or list-price decisions.
            </NeutralNote>
          )}

          {allUnder && !isStale && (
            <NeutralNote>
              Every tracked dealer is below your ${yourPrice.toFixed(2)} — street sits around ${Number(marketPricing?.streetPriceAvg ?? 0).toFixed(2)}
              {avgGap != null && avgGap > 0 ? `, about $${avgGap} under` : ''}.
            </NeutralNote>
          )}

          {dealers.length > 0 && (
            <Box sx={{ border: `1px solid ${tokens.line.hairline}`, borderRadius: '10px', overflow: 'hidden' }}>
              <ResponsiveRows
                columns={dealerColumns}
                rows={dealers}
                getRowKey={(dp) => `${dp.dealer}-${dp.url || ''}-${dp.price}`}
                gap={{ xs: '4px', md: '12px' }}
                padding="8px 12px"
                headerPadding="7px 12px"
                headerSx={{ bgcolor: tokens.surface.subtle }}
                headerCellSx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em' }}
                rowDivider="bottom"
                rowSx={() => ({ fontSize: '13px', borderBottom: `1px solid ${tokens.surface.sunken}` })}
                renderCell={(dp, columnKey) => {
                  if (columnKey === 'dealer') {
                    const url = getDealerUrl(dp.dealer, dp.url, product.brand, product.model || product.sku || '');
                    return (
                      <Typography sx={{ color: 'text.primary', fontSize: 'inherit', minWidth: 0 }}>
                        {url ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{dp.dealer}</a>
                        ) : dp.dealer}
                      </Typography>
                    );
                  }
                  if (columnKey === 'price') {
                    const under = yourPrice > 0 && dp.price != null && dp.price < yourPrice;
                    return (
                      <Typography sx={{ fontWeight: under ? 600 : 400, color: under ? tokens.signal.positive.ink : 'text.primary', fontVariantNumeric: 'tabular-nums', fontSize: 'inherit' }}>
                        ${Number(dp.price).toFixed(2)}
                      </Typography>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 0.6 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dp.inStock ? tokens.signal.positive.solid : tokens.ink.disabled }} />
                      <Typography sx={{ fontSize: '12.5px', color: dp.inStock ? tokens.signal.positive.ink : tokens.ink.muted }}>
                        {dp.inStock ? 'In stock' : dp.inStock === false ? 'Out of stock' : '—'}
                      </Typography>
                    </Box>
                  );
                }}
              />
            </Box>
          )}

          {(marketPricing?.msrp != null || marketPricing?.msrpSource) && (
            <Typography sx={{ fontSize: '11.5px', color: tokens.ink.faint }}>
              MSRP {marketPricing?.msrp != null ? `$${Number(marketPricing.msrp).toFixed(2)}` : '—'}
              {marketPricing?.msrpSource ? ` via ${marketPricing.msrpSource}` : ''}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography sx={{ fontSize: '13px', color: tokens.ink.muted }}>
          No dealer prices yet — refresh to look up current street pricing.
        </Typography>
      )}
    </Box>
  );
}
